import { createSelector } from 'reselect';
import { array, option } from 'fp-ts';
import { flow, identity } from 'fp-ts/function';
import { composeProps, createAction, createPropsExtender } from '@mews/framework';
import { replace } from '@mews/routing';
import { compactArray, getStringArrayProp, getStringProp, keyBy, primitiveTypeUniq } from '@mews/fp';
import { ApplicationState, CommanderThunkAction, AsyncAction } from '@mews-commander/types';
import {
    Reservation,
    MultipleReservationMakeOptionalParameters,
    MultipleReservationUndoCancellationParameters,
    ServiceOrderState,
} from '@mews-commander/data-access';
import { createFetchAction, setUrlParams } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import parentSelector from '../selector';
import prefix from '../prefix';
import { isReservationLockable } from '../utils/reservations';
import {
    createEntitySelectorByAvailabilityBlockId,
    createEntitySelectorByGroupId,
    createScopedBaseEntitySelectors,
    createScopedEntitiesReducer,
} from './utils';
import { createCurrentEnterpriseScopedAction } from './scopedActionCreators';
import { getCompanionshipsAccountIdsByReservationIds } from './companionships';

export const NAME = 'reservations';

const RESERVATIONS_LOADED = prefix('RESERVATIONS_LOADED');

const RESERVATION_CHANNEL_NUMBER_CHANGED = prefix('RESERVATION_CHANNEL_NUMBER_CHANGED');
const RESERVATION_PRODUCT_ORDER_CREATED = prefix('RESERVATION_PRODUCT_ORDER_CREATED');
const RESERVATION_SERVICE_ORDER_STATE_CHANGED = prefix('RESERVATION_SERVICE_ORDER_STATE_CHANGED');
const RESERVATION_LOCK_UPDATE = prefix('RESERVATION_LOCK_UPDATE');
const RESERVATION_UPDATE_OWNER = prefix('RESERVATION_UPDATE_OWNER');
const RESERVATION_CHECK_IN = prefix('RESERVATION_CHECK_IN');
const RESERVATION_UNDO_CANCELLATION = prefix('RESERVATION_UNDO_CANCELLATION');

interface ReservationIds {
    reservationIds: string[];
}

export const updateChannelNumber =
    ({
        reservationIds,
        channelNumber,
    }: ReservationIds & {
        channelNumber: string;
    }): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const promise = container().reservationComponent.updateChannelNumber(reservationIds, channelNumber);
        return dispatch(createAction(RESERVATION_CHANNEL_NUMBER_CHANGED, promise));
    };

export const addProductOrder =
    ({
        reservationIds,
        productId,
        count,
        consumedUtc,
    }: ReservationIds & {
        productId: string;
        count: number;
        consumedUtc: string;
    }): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const enterpriseId = getCurrentEnterpriseId();
        const productOrders = reservationIds.map((reservationId) => ({
            reservationId,
            productId,
            count,
            consumedUtc,
        }));
        const promise = container().reservationComponent.addProductOrder({ enterpriseId, productOrders });
        return dispatch(createAction(RESERVATION_PRODUCT_ORDER_CREATED, promise));
    };

export const makeOptional =
    (
        params: Pick<MultipleReservationMakeOptionalParameters, 'reservationIds' | 'sendEmail' | 'customEmail'>
    ): CommanderThunkAction =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.reservations.makeOptional(params);
        return dispatch(createAction(RESERVATION_SERVICE_ORDER_STATE_CHANGED, promise));
    };

export const updateOwner =
    (reservationId: string, customerId: string): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent.updateOwner(reservationId, customerId);
        return dispatch(createAction(RESERVATION_UPDATE_OWNER, promise));
    };
export const checkIn =
    (reservationIds: string[], disablePreauthorization = false): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent
            .checkIn(reservationIds, disablePreauthorization)
            .then(() => {
                dispatch(replace(setUrlParams({ Checkin: 'Succeeded' })));
            })
            .catch((err: Error) => {
                dispatch(replace(setUrlParams({ Checkin: 'Failed' })));
                throw err;
            });
        return dispatch(createFetchAction(RESERVATION_CHECK_IN, promise));
    };

export const updateLockedStatus =
    ({
        reservationIds,
        lockedStatus,
    }: ReservationIds & {
        lockedStatus: boolean;
    }): CommanderThunkAction =>
    async (dispatch, _, { container }) => {
        const { reservationComponent } = container();
        const promise = reservationComponent.updateLocked(reservationIds, lockedStatus);
        return dispatch(createAction(RESERVATION_LOCK_UPDATE, promise));
    };

export const setReservations = (reservations: Reservation[]) =>
    createCurrentEnterpriseScopedAction(RESERVATIONS_LOADED, { entities: reservations });

export const [getReservations, getReservationsByIds, getReservationsByOwnerId, getReservation] =
    createScopedBaseEntitySelectors<ApplicationState, Reservation, typeof NAME, 'enterpriseId'>(
        parentSelector,
        NAME,
        'enterpriseId'
    );
const moduleSelector = createSelector(parentSelector, (entities) => entities[NAME]);
export const getBaseReservations = createSelector(
    moduleSelector,
    flow(
        Object.values<Record<string, Reservation>>,
        array.flatMap(Object.values<Reservation>),
        keyBy<Reservation>('id'),
        identity<Record<string, Reservation>>
    )
);

export const getBaseReservation = createSelector(
    getStringProp('id'),
    getBaseReservations,
    (id, reservations): Reservation | undefined => reservations[id]
);

export const reservationsReducer = createScopedEntitiesReducer({
    FETCH_SUCCESS: RESERVATIONS_LOADED,
});

export const getReservationOwnerIds = createSelector(
    getReservationsByIds,
    array.map((r) => r.ownerId)
);

export const getReservationBookerAccountIds = createSelector(
    getReservationsByIds,
    flow(
        array.map((r) => r.bookerId),
        compactArray
    )
);

export const haveReservationsCommonBooker = flow(getReservationBookerAccountIds, (bookerIds) => bookerIds.length === 1);
export const getReservationsByGroupId = createEntitySelectorByGroupId(getReservations);

export const getReservationsByAvailabilityBlockId = createEntitySelectorByAvailabilityBlockId(getReservations);

export const getReservationGroupIdsByAvailabilityBlockId = createSelector(
    getReservationsByAvailabilityBlockId,
    array.map((r) => r.groupId)
);

export const getGroupNonCanceledReservations = createSelector(
    getReservationsByGroupId,
    array.filter((r) => r.serviceOrderState !== ServiceOrderState.Canceled)
);
export const getGroupLockableReservations = createSelector(
    getReservationsByGroupId,
    array.filter(isReservationLockable)
);
export const getGroupNonCanceledReservationsCount = createSelector(getGroupNonCanceledReservations, array.size);

export const getReservationsGroupServiceIds = createSelector(
    getReservationsByGroupId,
    flow(
        array.map((r) => r.serviceId),
        primitiveTypeUniq
    )
);

export const getReservationIdsByGroupId = createSelector(
    getReservationsByGroupId,
    array.map((r) => r.id)
);

export const getReservationsCustomerIds = createSelector(
    getReservationsByIds,
    composeProps(
        getCompanionshipsAccountIdsByReservationIds,
        createPropsExtender('reservationIds', getStringArrayProp('ids'))
    ),
    (reservations, companionIds) => {
        const accountIds = compactArray(reservations.map((r) => r.accountId));
        const bookerIds = array.filterMap(({ bookerId }: Reservation) => option.fromNullable(bookerId))(reservations);
        return primitiveTypeUniq([...accountIds, ...bookerIds, ...companionIds]);
    }
);
export const getReservationCustomerIds = createSelector(
    getReservation,
    composeProps(
        getCompanionshipsAccountIdsByReservationIds,
        createPropsExtender(
            'reservationIds',
            flow(getStringProp('id'), (reservationId) => [reservationId])
        )
    ),
    (reservation, companionAccountIds) =>
        primitiveTypeUniq(compactArray([reservation?.accountId, reservation?.bookerId, ...companionAccountIds]))
);
export const undoReservationCancellation =
    ({
        reservationIds,
        undoCancellationReason,
        reasonDetail,
    }: Pick<
        MultipleReservationUndoCancellationParameters,
        'reservationIds' | 'undoCancellationReason' | 'reasonDetail'
    >): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.reservations.undoCancellation({
            reservationIds,
            reasonDetail,
            undoCancellationReason,
        });
        return dispatch(createFetchAction(RESERVATION_UNDO_CANCELLATION, promise));
    };
